import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '../../i18n.js';
import {
  BASE_LANG,
  EMPTY_ISSUE,
  EMPTY_TRANSLATION,
  NEWSLETTER_STATUSES,
  TRANSLATION_LANGS,
  formatMonthName,
  validateIssue,
} from '../../lib/newsletter.js';
import Markdown from '../common/Markdown.jsx';

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

export default function NewsletterForm({ initialValue, submitLabel, onSubmit, onCancel, busy = false }) {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState(() => ({ ...EMPTY_ISSUE, ...initialValue }));
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(false);
  // Which language's title/summary/body the text fields are bound to.
  const [lang, setLang] = useState(BASE_LANG);

  useEffect(() => {
    if (initialValue) setForm({ ...EMPTY_ISSUE, ...initialValue });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue?.id]);

  function update(patch) {
    setForm((prev) => ({ ...prev, ...patch }));
    setError(null);
  }

  // Read/write the fields of the currently selected language. English maps
  // onto the base columns; everything else onto translations[lang].
  const isBase = lang === BASE_LANG;
  const current = isBase
    ? { title: form.title, summary: form.summary, body: form.body }
    : { ...EMPTY_TRANSLATION, ...(form.translations?.[lang] ?? {}) };

  function updateCurrent(patch) {
    if (isBase) {
      update(patch);
      return;
    }
    update({
      translations: {
        ...(form.translations ?? {}),
        [lang]: { ...current, ...patch },
      },
    });
  }

  function hasContent(code) {
    if (code === BASE_LANG) return !!form.body?.trim();
    return !!form.translations?.[code]?.body?.trim();
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const errKey = validateIssue(form);
    if (errKey) {
      setLang(BASE_LANG);
      setError(t(errKey));
      return;
    }
    try {
      await onSubmit(form);
    } catch (err) {
      // 23505 = unique_violation on (year, month)
      if (err?.code === '23505') setError(t('newsletter.form.errors.periodTaken'));
      else setError(err.message ?? String(err));
    }
  }

  const languages = SUPPORTED_LANGUAGES.filter((l) => l.code === BASE_LANG || TRANSLATION_LANGS.includes(l.code));

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <div className="grid gap-6 md:grid-cols-3">
        <Field label={t('newsletter.form.year')} required>
          <input
            type="number"
            min="2020"
            max="2100"
            value={form.year ?? ''}
            onChange={(e) => update({ year: e.target.value })}
            className="form-input"
          />
        </Field>
        <Field label={t('newsletter.form.month')} required>
          <select
            value={form.month ?? ''}
            onChange={(e) => update({ month: Number(e.target.value) })}
            className="form-input"
          >
            {MONTHS.map((m) => (
              <option key={m} value={m}>
                {formatMonthName(m, i18n.resolvedLanguage)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('newsletter.form.status')} required>
          <select
            value={form.status}
            onChange={(e) => update({ status: e.target.value })}
            className="form-input"
          >
            {NEWSLETTER_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`newsletter.status.${s}`)}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {/* Language strip */}
      <div>
        <span className="mb-2 block text-xs font-medium uppercase tracking-[0.12em] text-ink-secondary">
          {t('newsletter.form.languages')}
        </span>
        <div className="flex flex-wrap gap-2 border-b border-white/10 pb-3" role="tablist">
          {languages.map((l) => {
            const active = lang === l.code;
            const filled = hasContent(l.code);
            return (
              <button
                key={l.code}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setLang(l.code)}
                className={`flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm transition ${
                  active
                    ? 'border-gold/60 bg-navy-mid/60 text-gold-light'
                    : 'border-white/10 bg-navy-deep/40 text-ink-secondary hover:border-gold/30'
                }`}
              >
                <span aria-hidden="true">{l.flag}</span>
                {l.name}
                {l.code === BASE_LANG && (
                  <span className="text-[0.65rem] uppercase tracking-[0.12em] text-ink-muted">
                    {t('newsletter.form.original')}
                  </span>
                )}
                {l.code !== BASE_LANG && (
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${filled ? 'bg-emerald-400' : 'bg-white/20'}`}
                    aria-label={filled ? t('newsletter.form.translated') : t('newsletter.form.untranslated')}
                  />
                )}
              </button>
            );
          })}
        </div>
        {!isBase && (
          <p className="mt-2 text-xs text-ink-muted">{t('newsletter.form.translationHint')}</p>
        )}
      </div>

      <Field label={t('newsletter.form.title')} required={isBase}>
        <input
          type="text"
          lang={lang}
          value={current.title ?? ''}
          placeholder={isBase ? '' : form.title}
          onChange={(e) => updateCurrent({ title: e.target.value })}
          className="form-input"
        />
      </Field>

      <Field label={t('newsletter.form.summary')} hint={t('newsletter.form.summaryHint')}>
        <textarea
          rows={3}
          lang={lang}
          value={current.summary ?? ''}
          placeholder={isBase ? '' : form.summary ?? ''}
          onChange={(e) => updateCurrent({ summary: e.target.value })}
          className="form-input"
        />
      </Field>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="block text-xs font-medium uppercase tracking-[0.12em] text-ink-secondary">
            {t('newsletter.form.body')}
            {isBase && <span className="ml-1 text-gold">*</span>}
          </span>
          <div className="flex gap-1 rounded-full border border-white/10 p-0.5 text-xs">
            <ToggleButton active={!preview} onClick={() => setPreview(false)}>
              {t('newsletter.form.write')}
            </ToggleButton>
            <ToggleButton active={preview} onClick={() => setPreview(true)}>
              {t('newsletter.form.preview')}
            </ToggleButton>
          </div>
        </div>
        {preview ? (
          <div className="min-h-[24rem] rounded-md border border-white/10 bg-navy-deep/40 px-6 py-5" lang={lang}>
            {current.body?.trim() ? (
              <Markdown source={current.body} />
            ) : (
              <p className="text-sm text-ink-muted">{t('newsletter.form.previewEmpty')}</p>
            )}
          </div>
        ) : (
          <textarea
            rows={24}
            lang={lang}
            value={current.body ?? ''}
            onChange={(e) => updateCurrent({ body: e.target.value })}
            className="form-input font-mono text-[0.85rem]"
            spellCheck="true"
          />
        )}
        <span className="mt-1 block text-xs text-ink-muted">{t('newsletter.form.bodyHint')}</span>
      </div>

      {error && (
        <p className="rounded-md border border-red-500/40 bg-red-950/40 px-3 py-2 text-sm text-red-200">{error}</p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <button type="button" className="btn-ghost" onClick={onCancel} disabled={busy}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? t('common.loading') : submitLabel}
        </button>
      </div>
    </form>
  );
}

function ToggleButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-3 py-1 transition ${
        active ? 'bg-navy-mid/70 text-gold-light' : 'text-ink-secondary hover:text-cream'
      }`}
    >
      {children}
    </button>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-[0.12em] text-ink-secondary">
        {label}
        {required && <span className="ml-1 text-gold">*</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-ink-muted">{hint}</span>}
    </label>
  );
}
